import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../utils/useAuth";
import { createManagerNotification, createRefereeNotification } from "../utils/notifications";

export default function EditGame() {
  const { id } = useParams();
  const nav = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    teamA: "",
    teamB: "",
    date: "",
    time: "",
    venue: "",
    field: "",
    ageGroup: "",
    competition: "",
    needsReferee: false,
    needsAssistantReferee: false,
    needsAssistant4: false,
    needsAssistant5: false,
    needsTimeKeeper: false,
    needsPlayerMovementRecorder: false,
    needsStatistician: false,
    needsTechnicalZoneManager: false,
  });

  function updateField(key, value) {
    setForm({ ...form, [key]: value });
  }

  useEffect(() => {
    async function load() {
      const ref = doc(db, "games", id);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        setForm(snap.data());
      }
      setLoading(false);
    }
    load();
  }, [id]);

  async function save(e) {
    e.preventDefault();

    // Get the game before update to check for assigned referees
    const gameRef = doc(db, "games", id);
    const gameSnap = await getDoc(gameRef);
    const oldGame = gameSnap.data();

    await updateDoc(gameRef, {
      ...form
    });

    // Send notification to managers
    await createManagerNotification(
      "Game Updated",
      `${profile.fullName} updated game details: ${form.teamA} vs ${form.teamB} on ${form.date} at ${form.time}.`,
      profile.union
    );

    // Notify all assigned referees about the game edit
    const refereeIds = [
      oldGame.assignedRefereeId,
      oldGame.assistantReferee1Id,
      oldGame.assistantReferee2Id,
      oldGame.assistant4Id,
      oldGame.assistant5Id,
      oldGame.timeKeeperId,
      oldGame.playerMovementRecorderId,
      oldGame.statisticianId,
      oldGame.technicalZoneManagerId
    ].filter(Boolean);

    for (const refId of refereeIds) {
      await createRefereeNotification(
        refId,
        "Game Details Updated",
        `The game ${form.teamA} vs ${form.teamB} you're assigned to has been updated. New details: ${form.date} at ${form.time}, ${form.venue} (Field ${form.field})`
      );
    }

    alert("Game updated!");
    nav("/coach");
  }

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="max-w-xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Edit Game</h2>

      <form onSubmit={save} className="space-y-4">

        <input
          className="w-full p-2 border rounded"
          value={form.teamA}
          onChange={(e) => updateField("teamA", e.target.value)}
        />

        <input
          className="w-full p-2 border rounded"
          value={form.teamB}
          onChange={(e) => updateField("teamB", e.target.value)}
        />

        <input
          className="w-full p-2 border rounded"
          value={form.ageGroup}
          onChange={(e) => updateField("ageGroup", e.target.value)}
          placeholder="Age Group"
        />

        <select
          className="w-full p-2 border rounded"
          value={form.competition}
          onChange={(e) => updateField("competition", e.target.value)}
        >
          <option value="">Select Competition</option>
          <option value="Friendly">Friendly</option>
          <option value="League">League</option>
          <option value="Young guns">Young guns</option>
          <option value="Trials">Trials</option>
          <option value="Currie Cup">Currie Cup</option>
          <option value="Sa Cup">Sa Cup</option>
          <option value="Saru">Saru</option>
          <option value="Varsity Cup">Varsity Cup</option>
          <option value="7s">7s</option>
        </select>

        <input
          type="date"
          className="w-full p-2 border rounded"
          value={form.date}
          onChange={(e) => updateField("date", e.target.value)}
        />

        <input
          type="time"
          className="w-full p-2 border rounded"
          value={form.time}
          onChange={(e) => updateField("time", e.target.value)}
        />

        <input
          className="w-full p-2 border rounded"
          value={form.venue}
          onChange={(e) => updateField("venue", e.target.value)}
          placeholder="Venue"
        />

        <input
          className="w-full p-2 border rounded"
          value={form.field}
          onChange={(e) => updateField("field", e.target.value)}
          placeholder="Field"
        />

        <div className="mt-3">
          <h3 className="font-semibold mb-2">Required Match Officials:</h3>
          
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsReferee}
                onChange={(e) => updateField("needsReferee", e.target.checked)}
              />
              <span>Referee</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsAssistantReferee}
                onChange={(e) =>
                  updateField("needsAssistantReferee", e.target.checked)
                }
              />
              <span>Assistant Referee</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsAssistant4}
                onChange={(e) => updateField("needsAssistant4", e.target.checked)}
              />
              <span>Assistant 4</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsAssistant5}
                onChange={(e) => updateField("needsAssistant5", e.target.checked)}
              />
              <span>Assistant 5</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsTimeKeeper}
                onChange={(e) => updateField("needsTimeKeeper", e.target.checked)}
              />
              <span>Time Keeper</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsPlayerMovementRecorder}
                onChange={(e) => updateField("needsPlayerMovementRecorder", e.target.checked)}
              />
              <span>Player Movement Recorder</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsStatistician}
                onChange={(e) => updateField("needsStatistician", e.target.checked)}
              />
              <span>Statistician</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={form.needsTechnicalZoneManager}
                onChange={(e) => updateField("needsTechnicalZoneManager", e.target.checked)}
              />
              <span>Technical Zone Manager</span>
            </label>
          </div>
        </div>

        <button className="w-full bg-green-600 text-white p-2 rounded">
          Save Changes
        </button>
      </form>
    </div>
  );
}
